
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1400px] mx-auto px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/53a03105-c8fe-47ef-844a-fc3d645a5c97.png" alt="LOBAIR SL Logo" className="h-10 w-10 object-contain" />
            <span className={`text-xl font-semibold tracking-wide transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>LOBAIR SL</span>
          </div>
          <div className="flex gap-10">
            {['Home', 'About', 'Products', 'Features', 'Team', 'Contact'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className={`text-sm font-medium transition-colors hover:opacity-80 cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#1E1B4B] to-[#1E1B4B]"></div>
        
        {/* Floating Geometric Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-32 h-32 border-2 border-cyan-400/20 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 border-2 border-purple-400/20 rotate-45 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-40 left-1/4 w-40 h-40 border-2 border-blue-400/20 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-20 right-1/3 w-28 h-28 border-2 border-emerald-400/20 rotate-12 animate-pulse" style={{ animationDelay: '1.5s' }}></div>
        </div>

        {/* Glow Effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-blue-500/10 rounded-full blur-3xl"></div>

        <div className="relative z-10 text-center px-8 max-w-5xl mx-auto">
          <h1 className="text-7xl font-extrabold text-white mb-8 leading-tight" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
            CRAFTING PREMIUM<br />iOS EXPERIENCES
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Expert team developing modern applications<br />
            Enhancing security and user experience on iOS platform
          </p>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-[#F8F9FC]">
        <div className="max-w-[1200px] mx-auto px-20">
          <div className="grid grid-cols-5 gap-16">
            <div className="col-span-2">
              <div className="inline-block px-4 py-2 bg-white rounded-full shadow-sm mb-6">
                <span className="text-xs font-semibold text-gray-600 tracking-wider uppercase">Who We Are</span>
              </div>
              <h2 className="text-5xl font-bold text-[#0F172A] leading-tight">
                Building the<br />Future of<br />Mobile Security
              </h2>
            </div>
            <div className="col-span-3 relative">
              <div className="absolute left-0 top-0 bottom-1/2 w-1 bg-blue-500"></div>
              <div className="pl-8">
                <p className="text-base text-gray-600 leading-relaxed mb-6">
                  LOBAIR SL is a dedicated team of experts specializing in the development of cutting-edge applications for the iOS platform. Our mission is to create innovative solutions that not only meet but exceed the expectations of modern mobile users.
                </p>
                <p className="text-base text-gray-600 leading-relaxed">
                  With a focus on security, performance, and user experience, we develop products like VPN and Cleaner that empower users to take control of their mobile devices. Our commitment to excellence drives us to continuously innovate and deliver applications that make a real difference in the lives of iOS users worldwide.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-[#0F172A]">
        <div className="max-w-[1200px] mx-auto px-20">
          <h2 className="text-5xl font-bold text-white text-center mb-16">OUR SOLUTIONS</h2>
          <div className="grid grid-cols-2 gap-8">
            {/* VPN Card */}
            <div className="group bg-gradient-to-br from-[#1E3A8A] to-[#6B21A8] rounded-3xl p-12 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-6xl text-cyan-400" style={{ textShadow: '0 0 20px rgba(6, 182, 212, 0.5)' }}></i>
              </div>
              <h3 className="text-4xl font-bold text-white mb-6">VPN</h3>
              <ul className="space-y-4">
                <li className="text-sm text-gray-300 flex items-start">
                  <span className="mr-3 mt-1">•</span>
                  <span>Secure and encrypted connection for complete privacy</span>
                </li>
                <li className="text-sm text-gray-300 flex items-start">
                  <span className="mr-3 mt-1">•</span>
                  <span>Fast servers worldwide for seamless browsing</span>
                </li>
                <li className="text-sm text-gray-300 flex items-start">
                  <span className="mr-3 mt-1">•</span>
                  <span>User-friendly interface designed for iOS</span>
                </li>
              </ul>
            </div>

            {/* Cleaner Card */}
            <div className="group bg-gradient-to-br from-[#1E3A8A] to-[#6B21A8] rounded-3xl p-12 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl cursor-pointer">
              <div className="w-20 h-20 flex items-center justify-center mb-8">
                <i className="ri-brush-line text-6xl text-emerald-400" style={{ textShadow: '0 0 20px rgba(16, 185, 129, 0.5)' }}></i>
              </div>
              <h3 className="text-4xl font-bold text-white mb-6">Cleaner</h3>
              <ul className="space-y-4">
                <li className="text-sm text-gray-300 flex items-start">
                  <span className="mr-3 mt-1">•</span>
                  <span>Optimize device performance with smart cleaning</span>
                </li>
                <li className="text-sm text-gray-300 flex items-start">
                  <span className="mr-3 mt-1">•</span>
                  <span>Remove junk files and free up storage space</span>
                </li>
                <li className="text-sm text-gray-300 flex items-start">
                  <span className="mr-3 mt-1">•</span>
                  <span>Enhance battery life and system efficiency</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-20">
          <h2 className="text-5xl font-bold text-[#0F172A] text-center mb-4">CORE CAPABILITIES</h2>
          <div className="grid grid-cols-3 gap-6 mt-16">
            {[
              { icon: 'ri-lock-line', color: 'text-blue-500', title: 'Advanced Security', desc: 'Military-grade encryption and security protocols to protect user data and privacy at all times.' },
              { icon: 'ri-speed-line', color: 'text-purple-500', title: 'High Performance', desc: 'Optimized code and efficient algorithms ensure smooth operation without draining device resources.' },
              { icon: 'ri-user-heart-line', color: 'text-cyan-500', title: 'User-Centric Design', desc: 'Intuitive interfaces crafted with iOS design guidelines for seamless user experience.' },
              { icon: 'ri-refresh-line', color: 'text-emerald-500', title: 'Regular Updates', desc: 'Continuous improvements and feature additions to stay ahead of evolving user needs.' },
              { icon: 'ri-customer-service-line', color: 'text-orange-500', title: 'Expert Support', desc: 'Dedicated team ready to assist users with any questions or technical challenges.' },
              { icon: 'ri-shield-star-line', color: 'text-pink-500', title: 'Privacy First', desc: 'No data collection or tracking, ensuring complete privacy and peace of mind for users.' }
            ].map((feature, index) => (
              <div 
                key={index} 
                className="bg-white border border-gray-200 rounded-2xl p-10 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg cursor-pointer"
                style={{ minHeight: index % 3 === 1 ? '240px' : '200px' }}
              >
                <div className="w-6 h-6 flex items-center justify-center mb-4">
                  <i className={`${feature.icon} text-2xl ${feature.color}`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-32 bg-[#F1F5F9]">
        <div className="max-w-[1200px] mx-auto px-20">
          <h2 className="text-5xl font-bold text-[#0F172A] text-center mb-3">MEET THE EXPERTS</h2>
          <p className="text-base text-gray-600 text-center mb-16">Passionate professionals dedicated to iOS excellence</p>
          <div className="grid grid-cols-4 gap-6">
            {[
              { role: 'Lead iOS Developer', gradient: 'from-blue-400 to-purple-500' },
              { role: 'Security Specialist', gradient: 'from-cyan-400 to-blue-500' },
              { role: 'UX Designer', gradient: 'from-emerald-400 to-cyan-500' },
              { role: 'Product Manager', gradient: 'from-orange-400 to-pink-500' }
            ].map((member, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 text-center transition-all duration-300 hover:shadow-lg cursor-pointer">
                <div className={`w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br ${member.gradient}`}></div>
                <p className="text-sm text-gray-600">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-[#0F172A]">
        <div className="max-w-[1200px] mx-auto px-20">
          <h2 className="text-5xl font-bold text-white text-center mb-20">GET IN TOUCH</h2>
          <div className="grid grid-cols-2 gap-20">
            <div className="space-y-10">
              <div>
                <div className="text-xs font-semibold text-cyan-400 tracking-widest uppercase mb-2">Address</div>
                <div className="text-base text-white">CALLE LONDRES 4</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-cyan-400 tracking-widest uppercase mb-2">City</div>
                <div className="text-base text-white">Casarrubuelos</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-cyan-400 tracking-widest uppercase mb-2">Zip</div>
                <div className="text-base text-white">28977</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-cyan-400 tracking-widest uppercase mb-2">Country</div>
                <div className="text-base text-white">Spain</div>
              </div>
              <div>
                <div className="text-xs font-semibold text-cyan-400 tracking-widest uppercase mb-2">Email</div>
                <a href="mailto:info@lobairsl.com" className="text-base text-white hover:text-cyan-400 transition-colors cursor-pointer">info@lobairsl.com</a>
              </div>
              <div>
                <div className="text-xs font-semibold text-cyan-400 tracking-widest uppercase mb-2">Number</div>
                <a href="tel:+14472514588" className="text-base text-white hover:text-cyan-400 transition-colors cursor-pointer">+1 4472514588</a>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="w-4/5 h-80 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl opacity-20"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] border-t border-white/10 py-8">
        <div className="max-w-[1200px] mx-auto px-20 flex items-center justify-between">
          <div className="text-sm text-white font-medium">LOBAIR SL</div>
          <div className="text-xs text-gray-500">© 2025 All rights reserved</div>
          <div className="text-xs text-gray-500">Casarrubuelos, Spain</div>
        </div>
      </footer>
    </div>
  );
}
